import { trpc } from '@bitbot/api';
import { createStore } from 'hox';
import { useEffect, useState, useCallback } from 'react';
import { Notify } from '@bitbot/ui-design';
import { useWallets } from './list';
import { debounce } from 'lodash';
import { numberReg } from '@bitbot/shared/utils/regex';

export const [useCreateWallet, CreateWalletProvider] = createStore(() => {
  const [showSetWalletName, setShowSetWalletName] = useState(false);
  const [showCreateWalletDialog, setShowCreateWalletDialog] =
    useState(false);
  const [showDetail, setShowDetail] = useState(false);
  const [name, setName] = useState('');
  const [showNotEnough, setShowNotEnough] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const { getWallets, chain, setCurrentWallet, currentWallet } =
    useWallets();
  const [isCreatingWallet, setIsCreatingWallet] = useState(false);
  const [authCode, setAuthCode] = useState('');
  const editWalletMutation = trpc.wallet.changeWalletName.useMutation();
  const [settingName, setSettingName] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');
  const editWallet = () => {
    setSettingName(true);
    editWalletMutation
      .mutateAsync({
        wallet: currentWallet!,
        name,
      })
      .then(() => {
        getWallets(false);
        setTimeout(() => {
          setShowSetWalletName(false);
          setShowSuccess(true);
        }, 500);
      })
      .catch(e => {
        Notify.error(e.message);
      })
      .finally(() => {
        setSettingName(false);
      });
  };

  useEffect(() => {
    if (!showCreateWalletDialog) {
      setAuthCode('');
      setErrorMsg('');
    }
  }, [showCreateWalletDialog]);

  const _setAuthCode = (code: string) => {
    const v = code
      .replace(/,/, '.')
      .replace(/[^0-9]/g, '')
      .replace(/(\..*?)\..*/g, '$1');
    if (numberReg.test(v) && v.length <= 6) {
      setAuthCode(v);
      setErrorMsg('');
    }
  };
  const createWalletMutation = trpc.wallet.createWallet.useMutation();

  useEffect(() => {
    if (!showSetWalletName && !showNotEnough && !showSuccess) {
      setShowDetail(false);
      setName('');
    }
  }, [showSetWalletName, showNotEnough, showSuccess]);
  const createWallet = useCallback(
    debounce(() => {
      setIsCreatingWallet(true);
      createWalletMutation
        .mutateAsync({ chain: chain.name, otp: authCode })
        .then(data => {
          setTimeout(() => {
            getWallets(false);
          }, 500);
          setShowCreateWalletDialog(false);
          setShowSetWalletName(true);
          setCurrentWallet(data.address);
        })
        .catch(error => {
          if (error.message.includes('Invalid OTP')) {
            setErrorMsg('Incorrect code. Please re-enter.');
          } else if (error.message.includes('user points not enough')) {
            setShowCreateWalletDialog(false);
            setShowNotEnough(true);
          } else {
            Notify.error(error.message);
          }
        })
        .finally(() => {
          setIsCreatingWallet(false);
        });
    }, 300),
    [authCode],
  );

  return {
    showSetWalletName,
    setShowSetWalletName,
    showDetail,
    setShowDetail,
    name,
    setName,
    showNotEnough,
    setShowNotEnough,
    showSuccess,
    setShowSuccess,
    createWallet,
    isCreatingWallet,
    authCode,
    setAuthCode: _setAuthCode,
    showCreateWalletDialog,
    setShowCreateWalletDialog,
    editWallet,
    settingName,
    errorMsg,
  };
});

export const useEditWallet = (currentName: string) => {
  const editWalletMutation = trpc.wallet.changeWalletName.useMutation();
  const [name, setName] = useState(currentName);
  useEffect(() => {
    setName(currentName);
  }, [currentName]);
  const editWallet = (name: string, wallet: string) => {
    return editWalletMutation.mutateAsync({ wallet, name });
  };
  return {
    editWallet,
    name,
    setName,
  };
};
